import { Button, Modal, Pagination, PaginationItem, Tooltip } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { CustomMUIDataTable, ExpansionCell, NoItem, tableHeadRender } from './TableAssigned';
import { memo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { scholarshipService } from 'services';
import { GetScholarshipsParams, ScholarshipsHeroType } from 'models/Scholarship';
import { CardHeroMinimized } from 'views/Home/cards';
import { useSelector } from 'react-redux';
import { profileSelector } from 'reducers/profile';
import ScholarStatus from 'components/ScholarStatus';
import { useSnackbar } from 'notistack';
import PopupProcessing from './PopupProcessing';
import { shorten } from 'utils/common';
import { InfoOutlined } from '@mui/icons-material';
import { useWindowDimensions } from 'hooks';
import { Link } from 'react-router-dom';
import { OKG_HERO } from 'env';
import { publicRoute } from 'routes';

const handleRowHover = (rowIndex: any) => {
  let row = document.getElementById('hoverRow-tableScholarship-' + rowIndex);
  if (!!row) row!.style.display = 'flex';
};

const handleRowHoverLeave = (rowIndex: any) => {
  let row = document.getElementById('hoverRow-tableScholarship-' + rowIndex);
  if (!!row) row!.style.display = 'none';
};

const defaultSearch = {
  currentPage: 1,
  pageSize: 10,
  full: false,
  scholarshipWeb3AddressAssigned: false,
  isScholarshipActive: true,
};

const TableScholarship = () => {
  const { address } = useSelector(profileSelector);
  const { isMobile } = useWindowDimensions();
  const [dataSearch, setDataSearch] = useState({ ...defaultSearch, address });
  const { enqueueSnackbar } = useSnackbar();

  const { data: scholarships, refetch } = useQuery(
    ['scholarshipService.getScholarshipScholarScholarship', dataSearch],
    async ({ queryKey }) => {
      const scholarshipList = await scholarshipService.getScholarship(queryKey[1] as GetScholarshipsParams);
      const scholarshipsIdsList = scholarshipList?.items?.map(({ scholarshipId }) => scholarshipId).toString();
      const scholarStatus = await scholarshipService.getScholarStatus({
        scholarAddr: typeof queryKey[1] !== 'string' ? queryKey[1].address : '',
        scholarshipIds: scholarshipsIdsList,
      });
      const items = scholarshipList.items.map((item) => ({
        ...item,
        application: scholarStatus[item.scholarshipId],
        status: scholarStatus[item.scholarshipId]?.status ?? '',
      }));
      return { ...scholarshipList, items };
    },
    { keepPreviousData: true },
  );

  const { mutate: applyScholarship, isLoading: isLoadingApply } = useMutation(scholarshipService.applyScholarship, {
    onSuccess: () => {
      refetch();
      enqueueSnackbar('Apply successfully!', { variant: 'success' });
    },
    onError: (e: any) => {
      enqueueSnackbar(e.response?.data?.message ?? 'Apply failed!', { variant: 'error' });
    },
  });

  const { mutate: cancelApplication, isLoading: isLoadingCancel } = useMutation(scholarshipService.cancelApplication, {
    onSuccess: () => {
      refetch();
      enqueueSnackbar('Cancel successfully!', { variant: 'success' });
    },
    onError: (e: any) => {
      enqueueSnackbar(e.response?.data?.message ?? 'Cancel failed!', { variant: 'error' });
    },
  });

  const HoverButton = memo(({ rowIndex }: { rowIndex: number }) => {
    const chosenScholarship = scholarships?.items[rowIndex];

    return (
      <div
        className='flex gap-2 h-full px-4 py-2 items-center justify-center'
        style={{
          background: isMobile ? '#271911' : '#382B24',
          minWidth: isMobile ? 'auto' : 150,
        }}
      >
        {(!chosenScholarship?.application || chosenScholarship?.application?.status === 'canceled') &&
          chosenScholarship?.web3Address?.toLowerCase() !== address?.toLowerCase() && (
            <Button
              variant='contained'
              startIcon={<DoneIcon />}
              onClick={() => {
                if (chosenScholarship?.web3Address?.toLowerCase() === address?.toLowerCase()) {
                  enqueueSnackbar('Can not apply to your own scholarship!', { variant: 'error' });
                } else {
                  applyScholarship({ scholarshipId: chosenScholarship?.scholarshipId ?? '' });
                }
              }}
              className='h-9'
            >
              Apply
            </Button>
          )}
        {chosenScholarship?.web3Address?.toLowerCase() === address?.toLowerCase() && (
          <Button variant='contained' disabled className='h-9'>
            Your listing
          </Button>
        )}
        {chosenScholarship?.application?.status === 'none' && (
          <Button
            variant='contained'
            startIcon={<CloseIcon />}
            onClick={() => cancelApplication({ id: chosenScholarship?.application._id ?? '' })}
            className='h-9'
          >
            Cancel
          </Button>
        )}
      </div>
    );
  });

  const columns = [
    {
      name: 'scholarshipId',
      label: 'ID',
      options: {
        filter: true,
        sort: true,
        customHeadRender: tableHeadRender,
        customBodyRender: (value: any, { rowIndex, currentTableData }: any) => {
          const dataIndex = currentTableData[rowIndex].index;

          return (
            <>
              <Tooltip placement='top' title={<div className='text-lg text-color-primary'>{value}</div>}>
                <div>{shorten(value, 8, 0)}</div>
              </Tooltip>
              <div
                id={`hoverRow-tableScholarship-${dataIndex}`}
                className='absolute right-0'
                style={{
                  top: '50%',
                  transform: 'translate(0, -50%)',
                  display: 'none',
                }}
              >
                <HoverButton rowIndex={dataIndex} />
              </div>
            </>
          );
        },
      },
    },
    {
      name: 'scholarshipName',
      label: 'Scholarship name',
      options: {
        filter: true,
        sort: true,
        customHeadRender: tableHeadRender,
        customBodyRender: (value: any, { rowIndex }: any) => (
          <Tooltip placement='top' title={<div className='text-lg text-color-primary'>{value}</div>}>
            <div>{shorten(value, 25, 0, 25)}</div>
          </Tooltip>
        ),
      },
    },
    {
      name: 'web3Address',
      label: 'Created by',
      options: {
        filter: true,
        sort: true,
        customHeadRender: tableHeadRender,
        customBodyRender: (value: any, { rowIndex }: any) => (
          <Tooltip placement='top' title={<div className='text-lg text-color-primary'>{value}</div>}>
            <div>{shorten(value, 8, 0)}</div>
          </Tooltip>
        ),
      },
    },
    {
      name: 'userName',
      label: 'Account',
      options: {
        filter: true,
        sort: true,
        customHeadRender: tableHeadRender,
        customBodyRender: (value: any, { rowIndex }: any) => (
          <Tooltip placement='top' title={<div className='text-lg text-color-primary'>{value}</div>}>
            <div>{shorten(value, 15, 0, 15)}</div>
          </Tooltip>
        ),
      },
    },

    {
      name: 'heroes',
      label: 'Heroes',
      options: {
        filter: true,
        sort: true,
        customHeadRender: tableHeadRender,
        customBodyRender: (value: ScholarshipsHeroType[], { rowIndex }: any) => (
          <div className='flex gap-2'>
            {!!value &&
              value.map((hero: ScholarshipsHeroType, index: number) => (
                <div key={index}>
                  <Link to={publicRoute.heroView.url({ contract: OKG_HERO, tokenId: hero.tokenId })}>
                    <CardHeroMinimized item={hero} />
                  </Link>
                </div>
              ))}
          </div>
        ),
      },
    },
    {
      name: 'ratio',
      label: '',
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta: any, handleToggleColumn: any, sortOrder: any) =>
          tableHeadRender(columnMeta, handleToggleColumn, sortOrder, () => (
            <div className='flex gap-1 items-start'>
              Split Rate
              <Tooltip
                placement='top'
                title={
                  <div className='text-base text-color-secondary p-2'>Split Rate: % to scholar - % to manager</div>
                }
                componentsProps={{ tooltip: { className: 'bg-color-dark' }, arrow: { className: 'text-color-dark' } }}
              >
                <InfoOutlined style={{ fontSize: 15 }} />
              </Tooltip>
            </div>
          )),
        customBodyRender: (value: any, { rowIndex }: any) => <div>{`${value}/${100 - value}`}</div>,
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        customHeadRender: tableHeadRender,
        customBodyRender: (value: any, { rowIndex }: any) => (
          <div className='flex gap-2'>
            <ScholarStatus status={value ?? ''} />
          </div>
        ),
      },
    },
  ];

  const mobileColumn = [
    {
      name: 'scholarshipName',
      label: 'Scholarship',
      options: {
        filter: true,
        sort: true,
        customHeadRender: tableHeadRender,
        customBodyRender: (value: any, { rowIndex, currentTableData }: any) => {
          return (
            <>
              <Tooltip placement='top' title={<div className='text-lg text-color-primary'>{value}</div>}>
                <div>{shorten(value, 15, 0, 15)}</div>
              </Tooltip>
            </>
          );
        },
      },
    },
    {
      name: 'heroes',
      label: 'Heroes',
      options: {
        filter: true,
        sort: true,
        customHeadRender: tableHeadRender,
        customBodyRender: (value: ScholarshipsHeroType[], { rowIndex }: any) => (
          <div className='flex gap-2'>
            {!!value &&
              value.map((hero: ScholarshipsHeroType, index: number) => (
                <div key={index}>
                  <Link to={publicRoute.heroView.url({ contract: OKG_HERO, tokenId: hero.tokenId })}>
                    <CardHeroMinimized item={hero} />
                  </Link>
                </div>
              ))}
          </div>
        ),
      },
    },
    {
      name: 'ratio',
      label: 'Split Rate',
      options: {
        filter: true,
        sort: true,
        customHeadRender: tableHeadRender,
        customBodyRender: (value: any, { rowIndex }: any) => <div>{`${value}/${100 - value}`}</div>,
      },
    },
  ];

  return (
    <>
      {!!scholarships ? (
        scholarships.items.length === 0 ? (
          <NoItem />
        ) : (
          <>
            <div className='mb-3'>
              {isMobile ? (
                <CustomMUIDataTable
                  title=''
                  data={scholarships.items}
                  columns={mobileColumn}
                  options={{
                    selectableRows: undefined,
                    expandableRows: true,
                    responsive: 'standard',
                    elevation: 0,
                    download: false,
                    filter: false,
                    pagination: false,
                    search: false,
                    print: false,
                    viewColumns: false,
                    renderExpandableRow: (
                      rowData: string[],
                      rowMeta: {
                        dataIndex: number;
                        rowIndex: number;
                      },
                    ) => {
                      const expandScholarship = scholarships.items[rowMeta.dataIndex];

                      return (
                        <tr className='text-xs' style={{ background: '#1C110C' }}>
                          <td />
                          <td className='pr-12 py-2' colSpan={3}>
                            <div className='flex flex-col gap-2'>
                              <ExpansionCell
                                title='ID'
                                value={shorten(expandScholarship?.scholarshipId ?? '', 25, 0, 25)}
                              />
                              <ExpansionCell
                                title='Created by'
                                value={shorten(expandScholarship?.web3Address ?? '', 25, 0, 25)}
                                evenChild
                              />
                              <ExpansionCell
                                title='Account'
                                value={`${shorten(expandScholarship?.userName ?? '', 25, 0, 25)}`}
                              />
                              <ExpansionCell
                                title='Status'
                                value={
                                  <div className='flex gap-2'>
                                    <ScholarStatus status={expandScholarship?.status ?? ''} className='text-xs' />
                                  </div>
                                }
                                evenChild
                              />
                              <HoverButton rowIndex={rowMeta.dataIndex} />
                            </div>
                          </td>
                        </tr>
                      );
                    },
                  }}
                />
              ) : (
                <CustomMUIDataTable
                  title=''
                  data={scholarships.items}
                  columns={columns}
                  options={{
                    elevation: 0,
                    selectableRows: undefined,
                    download: false,
                    filter: false,
                    pagination: false,
                    search: false,
                    print: false,
                    viewColumns: false,
                    setRowProps: (row, dataIndex, rowIndex) => {
                      return {
                        className: 'relative',
                        onMouseEnter: () => handleRowHover(dataIndex),
                        onMouseLeave: () => handleRowHoverLeave(dataIndex),
                      };
                    },
                  }}
                />
              )}
            </div>
            <div className='flex justify-center'>
              <Pagination
                size='large'
                color='secondary'
                page={scholarships.paginationPage}
                count={scholarships.paginationCount}
                onChange={(event, page) => {
                  if (page > 0) setDataSearch({ ...dataSearch, currentPage: page });
                }}
                renderItem={(item) => <PaginationItem {...item} classes={{ root: 'font-bold' }} />}
              />
            </div>
            <Modal
              open={isLoadingCancel || isLoadingApply}
              style={{ background: '#06030280', backdropFilter: 'blur(4px)' }}
            >
              <>
                <PopupProcessing />
              </>
            </Modal>
          </>
        )
      ) : null}
    </>
  );
};

export default TableScholarship;
